import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../ui/dialog';
import {
  FileText, Plus, Trash2, GripVertical, ChevronDown, ChevronUp,
  Clock, Save, Users, Sparkles, ChevronRight,
} from 'lucide-react';

const OPERADORES_VISIVEIS_DEFAULT = 6;

const formatarNomeOperador = (nomeCompleto) => {
  if (!nomeCompleto) return '?';
  const partes = nomeCompleto.trim().split(' ');
  return partes.length === 1 ? partes[0] : `${partes[0]} ${partes[partes.length - 1]}`;
};

const formatTempo = (segundos) => {
  if (!segundos || segundos === 0) return '0s';
  if (segundos >= 60) {
    const min = Math.floor(segundos / 60);
    const sec = segundos % 60;
    return sec > 0 ? `${min}min ${sec}s` : `${min}min`;
  }
  return `${segundos}s`;
};

export default function ModalCriarMinuta({
  open,
  onOpenChange,
  initialData = null,          // { nome, descricao, operacoes[] }
  operacoes = [],
  categorias = [],
  allCategorias = [],
  operadores = [],
  loading = false,
  setLoading,
  onSave,                      // async (payload) => void
  filtrarOperadoresPorPermissao = null,
  existeOperadorComPermissao = false,
}) {
  // Estado interno do formulário
  const [form, setForm] = useState({ nome: '', descricao: '', operacoes: [] });
  const [draggedOp, setDraggedOp] = useState(null);
  const [dragOverOp, setDragOverOp] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [expandedOperadores, setExpandedOperadores] = useState({});

  // Sincronizar com initialData quando o modal abre
  useEffect(() => {
    if (open && initialData) {
      setForm(initialData);
    } else if (!open) {
      setExpandedCategories([]);
      setDraggedOp(null);
      setDragOverOp(null);
      setExpandedOperadores({});
    }
  }, [open, initialData]);

  const getOpInfo = (id) => operacoes.find((op) => op.id === id);

  // ── Handlers de operações ──────────────────────────────────────
  const removerOperacao = (index) => {
    setForm((prev) => ({ ...prev, operacoes: prev.operacoes.filter((_, i) => i !== index) }));
  };

  const removerOperacaoPorId = (id) => {
    setForm((prev) => ({ ...prev, operacoes: prev.operacoes.filter((op) => op.operacao_id !== id) }));
  };

  const toggleOperacao = (operacao) => {
    const exists = form.operacoes.find((op) => op.operacao_id === operacao.id);
    if (exists) { removerOperacaoPorId(operacao.id); return; }

    const opsMesmaCategoria = operacoes.filter(
      (o) => o.categoria === operacao.categoria || o.categoria_id === operacao.categoria_id
    );
    const indiceDentroCategoria = opsMesmaCategoria.findIndex((o) => o.id === operacao.id);
    const indiceGlobal = operacoes.findIndex((o) => o.id === operacao.id);

    const novaOp = {
      operacao_id: operacao.id,
      operacao_codigo: operacao.codigo,
      operacao_nome: operacao.nome,
      tempo_standard: operacao.tempo_standard,
      tempo_ajustado: operacao.tempo_standard,
      ordem: operacao.ordem ?? 0,
      indice_categoria: indiceDentroCategoria >= 0 ? indiceDentroCategoria : 999,
      indice_global: indiceGlobal,
      grupo: null,
      operadores_atribuidos: [],
      categoria: operacao.categoria,
      categoria_id: operacao.categoria_id,
    };

    const novasOps = [...form.operacoes, novaOp].sort((a, b) => {
      const catA = allCategorias.find((c) => c.nome === a.categoria || c.id === a.categoria_id);
      const catB = allCategorias.find((c) => c.nome === b.categoria || c.id === b.categoria_id);
      const ordemA = catA?.ordem ?? 999;
      const ordemB = catB?.ordem ?? 999;
      if (ordemA !== ordemB) return ordemA - ordemB;
      const iA = a.indice_categoria ?? a.indice_global ?? 999;
      const iB = b.indice_categoria ?? b.indice_global ?? 999;
      if (iA !== iB) return iA - iB;
      return (a.operacao_codigo || '').localeCompare(b.operacao_codigo || '');
    });

    setForm((prev) => ({ ...prev, operacoes: novasOps }));
  };

  const setTempo = (index, valor) => {
    setForm((prev) => {
      const ops = [...prev.operacoes];
      ops[index] = { ...ops[index], tempo_ajustado: parseInt(valor) || 0 };
      return { ...prev, operacoes: ops };
    });
  };

  const setGrupo = (index, grupo) => {
    setForm((prev) => {
      const ops = [...prev.operacoes];
      ops[index] = { ...ops[index], grupo: grupo || null };
      return { ...prev, operacoes: ops };
    });
  };

  const toggleOperador = (opIndex, operadorId) => {
    setForm((prev) => {
      const ops = [...prev.operacoes];
      const op = ops[opIndex];
      const current = op.operadores_atribuidos || [];
      ops[opIndex] = {
        ...op,
        operadores_atribuidos: current.includes(operadorId)
          ? current.filter((id) => id !== operadorId)
          : [...current, operadorId],
      };
      return { ...prev, operacoes: ops };
    });
  };

  const addCategoriaPers = (categoria) => {
    const toAdd = (categoria.operacoes_ids || [])
      .map((id) => operacoes.find((o) => o.id === id))
      .filter(Boolean)
      .filter((op) => !form.operacoes.some((o) => o.operacao_id === op.id));

    if (toAdd.length === 0) { toast.info('Todas as operações desta categoria já estão na minuta'); return; }

    const newOps = toAdd.map((op) => ({
      operacao_id: op.id, operacao_codigo: op.codigo, operacao_nome: op.nome,
      tempo_standard: op.tempo_standard, tempo_ajustado: op.tempo_standard,
      ordem: op.ordem || 0, grupo: null, operadores_atribuidos: [], categoria: op.categoria,
    }));

    const todas = [...form.operacoes, ...newOps].sort((a, b) => {
      const cA = allCategorias.find((c) => c.nome === a.categoria);
      const cB = allCategorias.find((c) => c.nome === b.categoria);
      if ((cA?.ordem ?? 999) !== (cB?.ordem ?? 999)) return (cA?.ordem ?? 999) - (cB?.ordem ?? 999);
      return (a.ordem || 0) - (b.ordem || 0);
    });

    setForm((prev) => ({ ...prev, operacoes: todas }));
    toast.success(`${toAdd.length} operações adicionadas de "${categoria.nome}"`);
  };

  // ── Drag & Drop ───────────────────────────────────────────────
  const onDragStart = (e, idx) => { setDraggedOp(idx); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e, idx) => { e.preventDefault(); if (draggedOp !== null && draggedOp !== idx) setDragOverOp(idx); };
  const onDrop = (e, idx) => {
    e.preventDefault();
    if (draggedOp === null || draggedOp === idx) return;
    const ops = [...form.operacoes];
    const [item] = ops.splice(draggedOp, 1);
    ops.splice(idx, 0, item);
    setForm((prev) => ({ ...prev, operacoes: ops }));
    setDraggedOp(null); setDragOverOp(null);
    toast.success('Ordem das operações atualizada');
  };
  const onDragEnd = () => { setDraggedOp(null); setDragOverOp(null); };

  // ── Save ──────────────────────────────────────────────────────
  const handleGuardar = async () => {
    if (!form.nome.trim()) { toast.error('Por favor, introduza um nome para a minuta'); return; }
    if (form.operacoes.length === 0) { toast.error('A minuta deve ter pelo menos uma operação'); return; }

    const payload = {
      nome: form.nome,
      descricao: form.descricao,
      operacoes: form.operacoes.map((op, idx) => ({
        operacao_id: op.operacao_id,
        operacao_codigo: op.operacao_codigo,
        operacao_nome: op.operacao_nome,
        tempo_standard: op.tempo_standard || 0,
        tempo_ajustado: op.tempo_ajustado || 0,
        ordem: op.ordem ?? idx,
        grupo: op.grupo || null,
        operadores_atribuidos: op.operadores_atribuidos || [],
      })),
    };

    setLoading && setLoading(true);
    try {
      await onSave(payload);
      onOpenChange(false);
      setForm({ nome: '', descricao: '', operacoes: [] });
    } finally {
      setLoading && setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[98vw] w-[98vw] h-[95vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-xl font-heading flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-600" />
            Nova Minuta de Operações
          </DialogTitle>
          <DialogDescription>
            Crie uma minuta (modelo) baseada nas operações desta OF. Pode editar, adicionar ou remover operações e operadores.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col overflow-hidden space-y-4 mt-2">
          {/* Nome e Descrição */}
          <div className="grid grid-cols-4 gap-4 flex-shrink-0">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-stone-700 mb-1">Nome da Minuta *</label>
              <Input
                placeholder="Ex: Linha de Camisas"
                value={form.nome}
                onChange={(e) => setForm((p) => ({ ...p, nome: e.target.value }))}
                className="rounded-lg"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-stone-700 mb-1">Descrição</label>
              <Input
                placeholder="Descrição opcional"
                value={form.descricao}
                onChange={(e) => setForm((p) => ({ ...p, descricao: e.target.value }))}
                className="rounded-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-5 gap-6 flex-1 min-h-0 overflow-hidden">
            {/* Operações Disponíveis (2/5) */}
            <div className="col-span-2 flex flex-col min-h-0">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-stone-700">
                  Operações Disponíveis ({operacoes.length})
                </label>
              </div>

              {allCategorias.filter((cat) => cat.operacoes_ids?.length > 0).length > 0 && (
                <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded-lg flex-shrink-0">
                  <p className="text-xs font-medium text-amber-700 mb-2">Adicionar por Categoria Personalizada:</p>
                  <div className="flex flex-wrap gap-1">
                    {allCategorias.filter((cat) => cat.operacoes_ids?.length > 0).map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => addCategoriaPers(cat)}
                        className="px-2 py-1 text-xs rounded-full border hover:bg-amber-100 transition-colors flex items-center gap-1"
                        style={{ borderColor: cat.cor || '#6b7280', color: cat.cor || '#6b7280' }}
                      >
                        <Plus className="h-3 w-3" />
                        {cat.nome} ({cat.operacoes_ids.length})
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="border rounded-lg p-2 flex-1 overflow-y-auto space-y-2">
                {allCategorias.map((cat) => {
                  const opsCateg = operacoes.filter((op) => op.categoria === cat.nome);
                  if (opsCateg.length === 0) return null;
                  const isExpanded = expandedCategories.includes(cat.id);
                  const numSel = opsCateg.filter((op) => form.operacoes.some((o) => o.operacao_id === op.id)).length;
                  const cor = cat.cor || '#6b7280';

                  return (
                    <div key={cat.id} className="border rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedCategories((prev) =>
                            prev.includes(cat.id) ? prev.filter((id) => id !== cat.id) : [...prev, cat.id]
                          )
                        }
                        className="w-full flex items-center justify-between p-2 bg-stone-50 hover:bg-stone-100 text-sm font-medium text-stone-700"
                        style={{ borderLeft: `3px solid ${cor}` }}
                      >
                        <div className="flex items-center gap-2">
                          <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                          <span className="capitalize">{cat.nome}</span>
                          <span className="text-xs text-stone-400">({opsCateg.length})</span>
                        </div>
                        {numSel > 0 && (
                          <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">{numSel} sel.</span>
                        )}
                      </button>
                      {isExpanded && (
                        <div className="p-1 space-y-1 max-h-48 overflow-y-auto">
                          {opsCateg.map((op) => {
                            const isSel = form.operacoes.some((o) => o.operacao_id === op.id);
                            return (
                              <button
                                key={op.id}
                                type="button"
                                onClick={() => toggleOperacao(op)}
                                className={`w-full flex items-center gap-2 p-2 rounded text-left text-sm transition-colors ${isSel ? 'bg-primary/10 text-primary' : 'hover:bg-stone-50'}`}
                              >
                                <span className="font-mono text-xs text-stone-400">{op.codigo}</span>
                                <span className="flex-1 truncate">{op.nome}</span>
                                <span className="text-xs text-stone-500">{formatTempo(op.tempo_standard)}</span>
                                {isSel && <ChevronRight className="h-4 w-4" />}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Operações Selecionadas (3/5) */}
            <div className="col-span-3 flex flex-col min-h-0">
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Operações na Minuta ({form.operacoes.length})
              </label>
              <div className="border rounded-lg p-3 flex-1 overflow-y-auto space-y-3">
                {form.operacoes.length === 0 ? (
                  <p className="text-center text-stone-400 py-8 text-sm">
                    Selecione operações à esquerda ou arraste para reordenar
                  </p>
                ) : (
                  form.operacoes.map((op, idx) => {
                    const opInfo = getOpInfo(op.operacao_id);
                    const operadoresAttr = op.operadores_atribuidos || [];
                    const tempo = op.tempo_ajustado || opInfo?.tempo_standard || 0;
                    const categoriaOp = allCategorias.find(
                      (c) => c.nome === (op.categoria || opInfo?.categoria)
                    );
                    const opsFiltrados =
                      existeOperadorComPermissao && filtrarOperadoresPorPermissao
                        ? filtrarOperadoresPorPermissao(op.operacao_id, categoriaOp?.id)
                        : operadores;
                    const isExpanded = expandedOperadores[`op-${idx}`];
                    const temMais = opsFiltrados.length > OPERADORES_VISIVEIS_DEFAULT;
                    const opsVisiveis = isExpanded
                      ? opsFiltrados
                      : opsFiltrados.slice(0, OPERADORES_VISIVEIS_DEFAULT);

                    return (
                      <div
                        key={`minuta-${op.operacao_id}-${idx}`}
                        className={`p-3 bg-white rounded-lg border transition-all cursor-move ${
                          op.grupo ? 'border-l-4 border-l-primary' : 'border-stone-100'
                        } ${draggedOp === idx ? 'opacity-50 scale-95' : ''} ${
                          dragOverOp === idx ? 'ring-2 ring-primary ring-offset-2' : ''
                        }`}
                        draggable
                        onDragStart={(e) => onDragStart(e, idx)}
                        onDragOver={(e) => onDragOver(e, idx)}
                        onDrop={(e) => onDrop(e, idx)}
                        onDragEnd={onDragEnd}
                      >
                        {/* Linha 1: ordenação, código, nome, tempo, grupo, remover */}
                        <div className="flex items-center gap-2 mb-2">
                          <GripVertical className="h-4 w-4 text-stone-300" />
                          <span className="text-xs font-bold text-white bg-primary rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                            {idx + 1}
                          </span>
                          <span className="font-mono text-xs font-bold px-2 py-0.5 bg-primary/10 text-primary rounded border border-primary/20">
                            {op.operacao_codigo || opInfo?.codigo}
                          </span>
                          <span className="text-sm font-medium flex-1 truncate">
                            {op.operacao_nome || opInfo?.nome}
                          </span>
                          {(op.categoria || opInfo?.categoria) && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] bg-stone-100 text-stone-600">
                              {op.categoria || opInfo?.categoria}
                            </span>
                          )}
                          <div className="flex items-center gap-1 bg-stone-50 px-2 py-1 rounded">
                            <Clock className="h-3 w-3 text-stone-400" />
                            <input
                              type="number"
                              value={tempo}
                              onChange={(e) => setTempo(idx, e.target.value)}
                              className="w-12 text-xs border-0 bg-transparent text-center font-medium"
                            />
                            <span className="text-xs text-stone-400">s</span>
                          </div>
                          <select
                            value={op.grupo || ''}
                            onChange={(e) => setGrupo(idx, e.target.value || null)}
                            className="text-xs border rounded px-2 py-1"
                          >
                            {['', 'A', 'B', 'C', 'D'].map((g) => (
                              <option key={g} value={g}>{g || '-'}</option>
                            ))}
                          </select>
                          <Button
                            type="button" variant="ghost" size="icon"
                            className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => removerOperacao(idx)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>

                        {/* Linha 2: Operadores */}
                        <div className="pl-7">
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className="text-xs text-stone-500 flex items-center gap-1 mr-1">
                              <Users className="h-3 w-3" /> Operadores:
                            </span>
                            {opsVisiveis.map((opr) => (
                              <button
                                key={opr.id}
                                type="button"
                                onClick={() => toggleOperador(idx, opr.id)}
                                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                  operadoresAttr.includes(opr.id)
                                    ? 'bg-primary text-white border-primary'
                                    : 'bg-white text-stone-600 border-stone-200 hover:border-primary hover:text-primary'
                                }`}
                              >
                                {formatarNomeOperador(opr.nome_completo) || opr.username}
                              </button>
                            ))}
                            {temMais && (
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedOperadores((prev) => ({
                                    ...prev,
                                    [`op-${idx}`]: !prev[`op-${idx}`],
                                  }))
                                }
                                className="flex items-center gap-0.5 px-2 py-1 text-xs text-primary hover:text-primary/80"
                              >
                                {isExpanded ? (
                                  <><ChevronUp className="h-3.5 w-3.5" /> menos</>
                                ) : (
                                  <><ChevronDown className="h-3.5 w-3.5" /> +{opsFiltrados.length - OPERADORES_VISIVEIS_DEFAULT}</>
                                )}
                              </button>
                            )}
                            {opsFiltrados.length === 0 && (
                              <span className="text-xs text-stone-400 italic">Sem operadores com permissão</span>
                            )}
                            {operadoresAttr.length > 0 && (
                              <span className="text-xs text-green-600 ml-1">
                                ({operadoresAttr.length} selecionado{operadoresAttr.length > 1 ? 's' : ''})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded-lg flex-shrink-0">
                <p className="text-xs text-purple-700 flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  <strong>Dica:</strong> Os operadores associados serão pré-selecionados quando carregar esta minuta numa nova OF.
                </p>
              </div>
            </div>
          </div>

          {/* Botões */}
          <div className="flex gap-3 pt-4 flex-shrink-0 border-t">
            <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleGuardar}
              disabled={loading || !form.nome.trim() || form.operacoes.length === 0}
              className="flex-1 rounded-full bg-purple-600 hover:bg-purple-700"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'A guardar...' : 'Guardar Minuta'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
